import { useState, useEffect, useCallback, useRef } from 'react';
import { getTradeSignalAnalysis } from '../../../api/tradeSignals';
import userTradeSignalsAPI from '../../../api/userTradeSignals';
import { visualStrategiesApi } from '../../../api/visualStrategies';
import { fetchTradesForSymbol, fetchNotesForTrade } from '../../../api/trades';

/**
 * Hook for fetching and managing market signals (system, user, and trade history)
 * 
 * @param {string} symbol - The market symbol to fetch data for
 * @returns {Object} - { signals, userSignals, trades, tradeNotes, loading, refresh }
 */
const useMarketSignals = (symbol) => {
  const [signals, setSignals] = useState([]);
  const [userSignals, setUserSignals] = useState([]);
  const [trades, setTrades] = useState([]);
  const [tradeNotes, setTradeNotes] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const mountedRef = useRef(true);

  const refresh = useCallback(async () => {
    if (!mountedRef.current) return;

    if (!symbol) {
      if (mountedRef.current) {
        setSignals([]);
        setUserSignals([]);
        setTrades([]);
        setTradeNotes({});
      }
      return;
    }

    setLoading(true);
    setError(null);

    // Fetch signals and trades in parallel - both can fail independently
    await Promise.allSettled([
      // Fetch system and user trade signals
      (async () => {
        try {
          const [systemResponse, userResponse, strategiesResponse] = await Promise.all([
            getTradeSignalAnalysis({ symbol }),
            // limit=500 covers a symbol's full history across all strategies. The
            // backend's default limit=50 truncates before the newest signals are
            // returned for active symbols — DDB-side ordering isn't strictly by
            // date, so a small page can drop today's signals behind older ones
            // from other strategies, hiding fresh trade markers from the chart.
            userTradeSignalsAPI.getUserTradeSignals({ symbol, limit: 500 }).catch(() => ({ data: { signals: [] } })),
            // Fetch the live strategies so we can hide signals from strategies the
            // user has since deleted. UserTradeSignal rows are intentionally NOT
            // scrubbed on strategy delete (historical record), but the chart should
            // reflect the user's current strategy set — they don't want to see
            // markers (or dropdown entries) for things they've thrown away.
            // Fall back to null on failure so we show all signals rather than none.
            visualStrategiesApi.listStrategies({ limit: 500 }).catch(() => null),
          ]);

          if (!mountedRef.current) return;
          setSignals(systemResponse?.results || []);

          const allUserSignals = userResponse?.data?.signals || [];

          // The cron writes signals with `strategy: \`visual_${assoc.strategyId}\``
          // (see dwlf-scheduled-jobs/src/handlers/dailyTradeSignalJob.js). Build a
          // matching set of allowed values so the comparison is direct.
          const liveStrategyValues = strategiesResponse?.strategies
            ? new Set(strategiesResponse.strategies.map((s) => `visual_${s.strategyId}`))
            : null;

          const filteredUserSignals = liveStrategyValues
            ? allUserSignals.filter((sig) => {
                const key = sig?.strategy ?? sig?.strategyId;
                return key ? liveStrategyValues.has(key) : false;
              })
            : allUserSignals;

          setUserSignals(filteredUserSignals);
        } catch (err) {
          if (!mountedRef.current) return;
          setError((prev) => prev || err);
          setSignals([]);
          setUserSignals([]);
        }
      })(),

      // Fetch trade journal trades (closed trades) for the symbol
      (async () => {
        try {
          const tradesResponse = await fetchTradesForSymbol(symbol, { status: 'closed' });

          if (!mountedRef.current) return;

          const rawTrades = Array.isArray(tradesResponse)
            ? tradesResponse
            : tradesResponse?.data?.trades || [];

          // Fetch notes for each trade in parallel and normalize
          const normalizedTrades = await Promise.all(
            rawTrades.map(async (t) => {
              const tradeId = t.id || t.tradeId;
              let notesArray = [];

              // Fetch individual notes if we have a trade ID
              if (tradeId) {
                try {
                  notesArray = await fetchNotesForTrade(tradeId);
                } catch {
                  // Silently fail for notes fetch
                  notesArray = [];
                }
              }

              return {
                id: tradeId,
                entryTime: t.entryTime || t.entry_time || t.openTime || t.open_time,
                exitTime: t.exitTime || t.exit_time || t.closeTime || t.close_time,
                entryPrice: t.entryPrice || t.entry_price || t.openPrice || t.open_price,
                exitPrice: t.exitPrice || t.exit_price || t.closePrice || t.close_price,
                side: t.side || t.direction || 'long',
                pnl: t.pnl || t.profit || t.realizedPnl || t.realized_pnl || 0,
                notesArray,
              };
            })
          );

          if (!mountedRef.current) return;

          // Build notes map from results
          const notesMap = {};
          normalizedTrades.forEach((trade) => {
            if (trade.id) {
              notesMap[trade.id] = trade.notesArray;
            }
          });

          setTrades(normalizedTrades);
          setTradeNotes(notesMap);
        } catch (err) {
          if (!mountedRef.current) return;
          setError((prev) => prev || err);
          setTrades([]);
          setTradeNotes({});
        }
      })(),
    ]);

    if (mountedRef.current) {
      setLoading(false);
    }
  }, [symbol]);

  useEffect(() => {
    mountedRef.current = true;
    refresh();
    return () => {
      mountedRef.current = false;
    };
  }, [symbol]);

  return {
    signals,
    userSignals,
    trades,
    tradeNotes,
    loading,
    refresh,
    error,
  };
};

export default useMarketSignals;
